/* eslint-disable no-underscore-dangle */
import { useState, ChangeEvent, KeyboardEvent } from 'react'
import { StringParam, useQueryParams } from 'use-query-params'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { useQueryList } from '@percent/utility'
import {
  SearchInput,
  Flex,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableProvider,
  TableSearchAndFilters,
  TablePagination,
  Menu,
  Icon,
  ButtonText,
  Button
} from '@percent/lemonade'
import { ErrorView } from '@percent/admin-dashboard/common/components/errorView/ErrorView'
import { PermissionGate } from '@percent/admin-dashboard/common/components/permissionGate/PermissionGate'
import { usePermissions } from '@percent/admin-dashboard/common/hooks'
import type { SectionItem, SectionProps } from '@percent/lemonade'
import { isDefined } from '@percent/admin-dashboard/common/utility'
import type { Group, GroupPermission } from '@percent/admin-dashboard/api/actions/groups/groups.types'
import { CreateGroupModal, useCreateGroupModal } from './CreateGroupModal'
import { DeleteGroupModal, useDeleteGroupModal } from './DeleteGroupModal'
import { EditGroupModal, useEditGroupModal } from './EditGroupModal'
import type { PBPPermissionsExpansion } from '@percent/admin-dashboard/services/pbpResponses'

export function Groups() {
  const { groupService } = useServices()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { canViewRolesOnAnyDomain } = usePermissions()
  const [queryParams, setQueryParams] = useQueryParams({ search: StringParam })
  const [searchValue, setSearchValue] = useState(queryParams.search ?? '')

  const [{ data, isLoading, totalResults, errorMessage, status }, { nextPage, previousPage, refresh }] = useQueryList(
    ['/groups'],
    groupService.getGroups,
    {
      ...(queryParams.search && { search: queryParams.search })
    }
  )

  const createGroupModal = useCreateGroupModal({ onSuccess: refresh })
  const editGroupModal = useEditGroupModal({ onSuccess: refresh })
  const deleteGroupModal = useDeleteGroupModal({ onSuccess: refresh })

  const handleKeyChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value)
  }

  const handleKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      setQueryParams({ search: (event.target as HTMLInputElement).value })
    }

    if (event.key === 'Escape') {
      setSearchValue('')
      setQueryParams({ search: undefined })
    }
  }

  const handleClearValue = () => {
    setSearchValue('')
    setQueryParams({ search: undefined })
  }

  const menuItem = (
    key: string,
    label: string,
    onClick: () => void,
    dataTestId: string,
    visible = true
  ): SectionItem | undefined => (visible ? { key, label, onClick, dataTestId } : undefined)

  const getMenuItems = (group: Group & PBPPermissionsExpansion<GroupPermission>): SectionProps[] => [
    {
      items: [
        menuItem(
          'edit',
          t('table.groups.menu.editGroup'),
          () => editGroupModal.openModal({ id: group.id, name: group.name, description: group.description }),
          `group-edit-${group.id}`,
          group._permissions.can_edit
        ),
        menuItem(
          'delete',
          t('table.groups.menu.deleteGroup'),
          () => deleteGroupModal.openModal({ id: group.id, name: group.name }),
          `group-delete-${group.id}`,
          group._permissions.can_delete
        )
      ].filter(isDefined)
    }
  ]

  const columns = [
    { id: t('table.groups.name'), isSortable: false, props: { width: '40%' } },
    { id: t('table.groups.members'), isSortable: false, props: { width: '20%' } },
    { id: t('table.groups.permissions'), isSortable: false, props: { width: '30%' } },
    { id: '', isSortable: false, props: { width: '10%' } }
  ]

  if (errorMessage && status === 'error') {
    return <ErrorView errorMessage={errorMessage} />
  }

  return (
    <>
      <TableProvider
        columns={columns}
        totalResults={totalResults}
        isLoading={isLoading}
        illustration="no-results"
        emptyTableTitle={t('table.groups.emptyTable.title')}
        emptyTableDescription={t('table.groups.emptyTable.description')}
      >
        <TableSearchAndFilters>
          <Flex direction="row" gap="md" align="center" w="100%">
            <SearchInput
              data-testid="groups-search-input"
              placeholder={t('table.groups.searchPlaceholder')}
              value={searchValue}
              onChange={handleKeyChange}
              onKeyDown={handleKeyPress}
              handleClearValue={handleClearValue}
              width="100%"
            />
            <PermissionGate permissions="groups:can_create_group">
              <Button variant="primary" onClick={createGroupModal.openModal} data-testid="create-group-button">
                {t('button.createGroup')}
              </Button>
            </PermissionGate>
          </Flex>
        </TableSearchAndFilters>
        <Table>
          <TableBody>
            {data?.map(group => (
              <TableRow key={group.id}>
                <TableCell>
                  <ButtonText
                    onClick={() => navigate(`/accounts/groups/${group.id}`)}
                    data-testid={`group-name-${group.id}`}
                  >
                    {group.name}
                  </ButtonText>
                </TableCell>
                <TableCell>
                  {group.memberCount && group.memberCount > 0 ? (
                    group.memberCount
                  ) : (
                    <ButtonText
                      onClick={() => navigate(`/accounts/groups/${group.id}/members`)}
                      data-testid={`add-members-${group.id}`}
                    >
                      {t('table.groups.addMembers')}
                    </ButtonText>
                  )}
                </TableCell>
                <TableCell>
                  {group.roleCount && group.roleCount > 0 ? (
                    t('table.groups.rolesCount', { count: group.roleCount })
                  ) : canViewRolesOnAnyDomain ? (
                    <ButtonText
                      onClick={() => navigate(`/accounts/groups/${group.id}/permissions`)}
                      data-testid={`set-permissions-${group.id}`}
                    >
                      {t('table.groups.setPermissions')}
                    </ButtonText>
                  ) : (
                    t('table.groups.rolesCount', { count: 0 })
                  )}
                </TableCell>
                <TableCell>
                  <Menu
                    sections={getMenuItems(group)}
                    customTriggerComponent={
                      <span style={{ cursor: 'pointer' }} data-testid={`group-menu-${group.id}`}>
                        <Icon name="menu-vertical" size={5} />
                      </span>
                    }
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          totalResultsText={t('table.results', { count: totalResults ?? 0 })}
          previousPage={previousPage}
          nextPage={nextPage}
        />
      </TableProvider>
      <CreateGroupModal
        isOpen={createGroupModal.isOpen}
        onClose={createGroupModal.closeModal}
        onSuccess={createGroupModal.onSuccess}
      />
      {editGroupModal.modalData && (
        <EditGroupModal
          isOpen={editGroupModal.isOpen}
          onClose={editGroupModal.closeModal}
          groupId={editGroupModal.modalData.id}
          groupName={editGroupModal.modalData.name}
          groupDescription={editGroupModal.modalData.description}
          onSuccess={editGroupModal.onSuccess}
        />
      )}
      {deleteGroupModal.modalData && (
        <DeleteGroupModal
          isOpen={deleteGroupModal.isOpen}
          onClose={deleteGroupModal.closeModal}
          groupId={deleteGroupModal.modalData.id}
          groupName={deleteGroupModal.modalData.name}
          onSuccess={deleteGroupModal.onSuccess}
        />
      )}
    </>
  )
}
